import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { environment } from 'src/environments/environment';
import { Document, DocumentContainerDetails, DocumentGroup } from '../../../models/document.model';
import { AiDocumentInfo } from 'src/app/models/ai-doc-info';
import { DocumentConclusionGroup } from 'src/app/models/document-conclusion-group.model';
import { AuthService } from '../../auth.service';
import { JobDigitalLogFormInfo } from 'src/app/models/job-digital-log-form-info.model';
import { map, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class DocumentsService {

	private baseUrl = `${environment.baseUrl}/document`;

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService
		) { }

	@ApiMethod()
	getDocumentSummary(token: string): Observable<Document[]> {
		return this.httpClient.get<Document[]>(`${this.baseUrl}/${token}/documents`);
	}

	@ApiMethod()
	getDocumentContainerGroupSummary(token: string): Observable<DocumentGroup[]> {
		return this.httpClient.get<DocumentGroup[]>(`${this.baseUrl}/${token}/group-documents`);
	}

	@ApiMethod()
	getDocumentContainerDetails(token: string): Observable<DocumentContainerDetails> {
		return this.httpClient.get<DocumentContainerDetails>(`${this.baseUrl}/${token}/details`);
	}

	getDLFormDocuments(token: string): Observable<JobDigitalLogFormInfo[]> {
		return this.httpClient.get<JobDigitalLogFormInfo[]>(`${this.baseUrl}/${token}/photo-viewer`);
	}

	@ApiMethod()
	getDocument(token: string, documentId: number): Observable<Blob> {
		return this.httpClient.get<Blob>(`${this.baseUrl}/${token}/documents/${documentId}`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	getPhotoViewerDocument(token: string, documentId: number): Observable<Blob> {
		return this.httpClient.get<Blob>(`${this.baseUrl}/${token}/photo-viewer/${documentId}`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	getDocumentsZip(token: string): Observable<Blob[]> {
		return this.httpClient.get<Blob[]>(`${this.baseUrl}/${token}/documents/zip`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	getPhotoViewerDocumentsZip(token: string): Observable<Blob[]> {
		return this.httpClient.get<Blob[]>(`${this.baseUrl}/${token}/photo-viewer/documents/zip`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	getDocumentByOid(oid: string, documentId: number, isThumbnail: boolean): Observable<HttpResponse<Blob>> {
		if (isThumbnail) {
			return this.httpClient.get<Blob>(`${this.baseUrl}/viewdoc/${oid}/${documentId}/th`, { observe: 'response', responseType: 'blob' as 'json' });
		} else {
			return this.httpClient.get<Blob>(`${this.baseUrl}/viewdoc/${oid}/${documentId}`, { observe: 'response', responseType: 'blob' as 'json' });
		}
	}
	@ApiMethod()
	getDownloadDocument(oid: string, documentId: number): Observable<Blob> {
		return this.httpClient.get<Blob>(`${this.baseUrl}/viewdoc/${oid}/${documentId}`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	isDocumentAi(documentId: number): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/viewdoc/${documentId}/isAI`);
	}

	@ApiMethod()
	aiDocumentInfo(documentId: number): Observable<AiDocumentInfo> {
		return this.httpClient.get<AiDocumentInfo>(`${this.baseUrl}/viewdoc/${documentId}/ai-doc-info`);
	}

	getConclusionGroup(jobId: number, documentClassAndTypeMappingId: number, sourceAccountId?: number): Observable<DocumentConclusionGroup> {
		return this.httpClient.get<DocumentConclusionGroup>(`${this.baseUrl}/conclusionGroups/${jobId}/${documentClassAndTypeMappingId}/${sourceAccountId}`, { headers: this.authService.getAuthenticationHeader() });
	}
}
